import { useCallback, useEffect, useState } from "react";

export type Theme = "dark" | "light";

const STORAGE_KEY = "workloop:theme";

export function readTheme(): Theme {
  if (typeof window === "undefined") return "dark";
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "light" || stored === "dark") return stored;
  } catch {
    /* storage blocked (e.g. iframe/private mode SecurityError) — use default */
  }
  return "dark";
}

export function writeTheme(theme: Theme) {
  if (typeof document === "undefined") return;
  if (theme === "light") {
    document.documentElement.setAttribute("data-theme", "light");
  } else {
    document.documentElement.removeAttribute("data-theme");
  }
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    /* storage blocked — ignore */
  }
}

export function toggleThemeGlobal(): Theme {
  const next = readTheme() === "dark" ? "light" : "dark";
  writeTheme(next);
  return next;
}

let shortcutInstalled = false;

/** Install the Cmd/Ctrl+Shift+T global shortcut. Idempotent. */
export function installThemeShortcut() {
  if (shortcutInstalled || typeof window === "undefined") return;
  shortcutInstalled = true;
  window.addEventListener("keydown", (e) => {
    if (
      (e.metaKey || e.ctrlKey) &&
      e.shiftKey &&
      e.key.toLowerCase() === "t"
    ) {
      e.preventDefault();
      toggleThemeGlobal();
    }
  });
}

/**
 * Reactive theme hook. Syncs with external flips via MutationObserver
 * on the data-theme attribute (so keyboard shortcuts + palette toggles
 * propagate cleanly).
 */
export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(readTheme);

  useEffect(() => {
    writeTheme(theme);
  }, [theme]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === "attributes" && m.attributeName === "data-theme") {
          setThemeState(readTheme());
        }
      }
    });
    observer.observe(document.documentElement, { attributes: true });
    return () => observer.disconnect();
  }, []);

  const setTheme = useCallback((t: Theme) => setThemeState(t), []);
  const toggleTheme = useCallback(
    () => setThemeState((t) => (t === "dark" ? "light" : "dark")),
    [],
  );

  return { theme, setTheme, toggleTheme };
}
